// Inbound Requests combo: one stacked bar per time slot (Originals + Duplicates + the
// ping-shield event types) over a cyan area-fill Requests line drawn behind the stack.
// Mirrors calls_receivables_and_payables_combo.js.
import { Chart } from 'chart.js'

import { chartPalette, barDepthFill, barCapBorderRadius, verticalGradient } from './palette'

// Fixed shade per ping-shield status, assigned by dataset order (indices 3+ in the source
// data) rather than by label — the backend always emits the same status order.
const PING_SHIELD_COLOR_KEYS = ['pingShieldLight', 'pingShieldMid', 'pingShieldDark']

function seriesByLabel(datasets, label) {
    const match = datasets.find((dataset) => dataset.label === label)
    return (match && match.data) || []
}

export function buildInboundRequestsAndDuplicatesCombo(
    canvas,
    optionsFromParam,
    { plugins = [] } = {},
) {

    const { datasets: sourceDatasets = [], labels = [] } = optionsFromParam.data || {}
    const palette = chartPalette()

    const requestsLabel = sourceDatasets[0]?.label
    const originalsLabel = sourceDatasets[1]?.label
    const duplicatesLabel = sourceDatasets[2]?.label
    const pingShieldDatasets = sourceDatasets.slice(3)

    const requestsCounts = seriesByLabel(sourceDatasets, requestsLabel)
    const originalsCounts = seriesByLabel(sourceDatasets, originalsLabel)
    const duplicatesCounts = seriesByLabel(sourceDatasets, duplicatesLabel)

    // Every bar shares one stack, so each time slot renders as a single composition column:
    // Originals at the base, Duplicates, then the ping-shield types on top. The explicit stack
    // id also keeps the Requests line (stack undefined) out of barCapBorderRadius's
    // top-of-stack lookup.
    const barBase = {
        type: 'bar',
        yAxisID: 'y',
        barPercentage: 0.92,
        categoryPercentage: 0.78,
        stack: 'inbound',
        order: 2,
    }

    const datasets = [
        {
            ...barBase,
            label: originalsLabel,
            data: originalsCounts,
            backgroundColor: barDepthFill(palette.green),
            borderColor: palette.green,
        },
        {
            ...barBase,
            label: duplicatesLabel,
            data: duplicatesCounts,
            backgroundColor: barDepthFill(palette.blue),
            borderColor: palette.blue,
        },
        // Flat translucent fills (no barDepthFill): the depth helpers strip the alpha channel,
        // and the flat, see-through look is what keeps the shielded block visually recessive
        // next to the solid Originals/Duplicates segments.
        ...pingShieldDatasets.map((dataset, index) => {
            const color = palette[PING_SHIELD_COLOR_KEYS[index % PING_SHIELD_COLOR_KEYS.length]]
            return {
                ...barBase,
                label: dataset.label,
                data: dataset.data,
                backgroundColor: color,
                borderColor: color,
            }
        }),
    ]

    datasets.push({
        type: 'line',
        label: requestsLabel,
        yAxisID: 'y',
        data: requestsCounts,
        borderColor: palette.callsLine,
        backgroundColor: verticalGradient([
            [0, palette.areaFillTop],
            [1, palette.areaFillBottom],
        ]),
        tension: 0.35,
        pointRadius: 0,
        pointHoverRadius: 4,
        pointBackgroundColor: palette.callsLine,
        pointBorderColor: '#fff',
        pointBorderWidth: 1.5,
        borderJoinStyle: 'round',
        fill: true,
        // Higher order than the bars (2) draws the line first, i.e. behind the stack —
        // its area fill shows through the translucent ping-shield segments.
        order: 3,
    })

    return new Chart(canvas, {
        plugins: [...plugins],
        data: { labels, datasets },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            resizeDelay: 0,
            interaction: { mode: 'index', intersect: false },
            plugins: {
                legend: { display: false },
                tooltip: {
                    callbacks: {
                        label: (item) => {
                            const value = Number(item.parsed.y).toLocaleString()
                            return `${item.dataset.label}: ${value}`
                        },
                    },
                },
            },
            scales: {
                x: {
                    stacked: true,
                    grid: { drawTicks: false },
                    ticks: {
                        maxRotation: 0,
                        autoSkip: true,
                        maxTicksLimit: 12,
                    },
                },
                y: {
                    stacked: true,
                    beginAtZero: true,
                    grid: { drawTicks: false },
                    border: { display: false },
                    ticks: { padding: 8 },
                },
            },
            elements: {
                bar: {
                    borderRadius: barCapBorderRadius(false, true),
                    borderSkipped: false,
                },
            },
            animation: { duration: 320, easing: 'easeOutCubic' },
        },
    })
}
